/**
 * Housing Unit Detail Plugin for Native Interactive Site Map
 *
 * Owns [data-panel] content when activeView === VIEW_HOUSING_UNIT_DETAIL: sticky header, Lot &
 * Elevation / Floor Plan tabs, footer, and floor plan image behavior (desktop secondary panel
 * and mobile full-screen viewer).
 *
 * @module nativeInteractiveSiteMap/plugins/housingUnitDetail.plugin
 */

import { distinctUntilChanged, map, filter } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import createHousingUnitDetailHeader from '../components/housingUnitDetailHeader.dom';
import createTabsDom from '../components/tabs.dom';
import createLotAndElevationDom from '../components/lotAndElevationHousingUnit.dom';
import createFloorPlanConfigurationDom from '../components/floorPlanConfiguration.dom';
import createHousingUnitDetailFooterDom from '../components/housingUnitDetailFooter.dom';
import createSecondaryPanelController from '../components/secondaryPanelController.dom';
import createFloorPlanViewer from '../components/floorPlanViewer.dom';
import renderViewFloorPlanButton from '../components/viewFloorPlanButton.dom';
import createTabInteractionHandlers from '../handlers/tabInteractionHandlers';
import {
  selectActiveView,
  selectActiveHousingUnit,
  selectCurrentlySelectedHousingUnitLot,
  selectSearchParameters,
} from '../redux/selectors';
import { detailTabClicked$ } from '../redux/observables';
import createHousingUnitCardSelectedEpic from '../redux/housingUnitCardSelectedEpic';
import registerEpicOnce from '../redux/registerEpicOnce';
import LotStatus from '../enums/lotStatus';
import { deriveActiveChip } from '../services/mapChipService';
import {
  VIEW_HOUSING_UNIT_DETAIL,
  TAB_LOT_AND_ELEVATION,
  TAB_FLOOR_PLAN_CONFIG,
} from '../constants';

/**
 * Chips whose cards are allowed to navigate here — the same statuses
 * services/lotRoutingService.js#resolveLotDestination treats as eligible for a lot click, so a
 * Model home's card (rendered via the QMI card component inside lotMapSelection.plugin.js's
 * mobile preview, when the lot has a housingUnitId) can now reach housingUnitDetail the same way
 * an Available QMI card already could — see NHS20-8823.
 */
const CARD_CLICK_ELIGIBLE_CHIPS = [
  LotStatus.Available.toString(),
  LotStatus.Model.toString(),
  LotStatus.Sold.toString()];

export default function createHousingUnitDetailPlugin() {
  const state = {
    header: null,
    panel: null,
    tabs: null,
    selectedTab: TAB_LOT_AND_ELEVATION,
    lotAndElevationContent: null,
    configContent: null,
    footer: null,
    secondaryPanelController: null,
    mapContainer: null,
    floorPlanViewer: null,
    isMobile: false,
    isImageExpanded: false,
    // No structural-options selector exists yet for housing units either — see the equivalent
    // comment in floorPlanDetail.plugin.js.
    selectedStructuralOptions: null,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    cardClickSubscription: null,
    subscriptions: [],
  };

  function setupCardClickListener() {
    const cardSelector = '[data-housing-unit-id]';

    state.cardClickSubscription = fromEvent(state.panel, 'click')
      .pipe(
        map((event) => event.target?.closest?.(cardSelector) ?? null),
        // Only respond to clicks on cards if the active chip is one that can actually navigate
        // here (Available QMI card, or a Model home card — see CARD_CLICK_ELIGIBLE_CHIPS above)
        filter(() => CARD_CLICK_ELIGIBLE_CHIPS.includes(deriveActiveChip(selectSearchParameters(state.getState())))),
        filter((cardElement) => cardElement !== null),
        map((cardElement) => parseInt(cardElement.getAttribute('data-housing-unit-id'), 10)),
        filter((housingUnitId) => Number.isFinite(housingUnitId)),
      )
      .subscribe((housingUnitId) => state.dispatch(state.dataActions.housingUnitCardSelected(housingUnitId)));
  }

  function teardownCardClickListener() {
    if (state.cardClickSubscription) {
      state.cardClickSubscription.unsubscribe();
      state.cardClickSubscription = null;
    }
  }

  // Creates the secondary panel only while the Configuration tab is active, destroying it the
  // rest of the time (including while on the Lot & Elevation tab, or once this view is left — see
  // remove() below) rather than just hiding it — see secondaryPanelController.dom.js for why.
  function syncSecondaryPanel() {
    if (state.isMobile) {
      return;
    }

    if (state.selectedTab !== TAB_FLOOR_PLAN_CONFIG) {
      state.secondaryPanelController?.remove();
      state.secondaryPanelController = null;
      return;
    }

    if (!state.mapContainer) {
      return;
    }

    if (!state.secondaryPanelController) {
      state.secondaryPanelController = createSecondaryPanelController(state.mapContainer);
    }

    const activeHousingUnit = selectActiveHousingUnit(state.getState());
    state.secondaryPanelController.show({ floorplans: activeHousingUnit?.floorplans });
  }

  function syncFooter() {
    const shouldShowFooter = state.selectedTab === TAB_LOT_AND_ELEVATION && !state.isImageExpanded;
    const activeHousingUnit = selectActiveHousingUnit(state.getState());

    if (!shouldShowFooter || !activeHousingUnit) {
      state.footer?.remove();
      state.footer = null;
      return;
    }

    const lot = selectCurrentlySelectedHousingUnitLot(state.getState());

    state.footer?.remove();
    state.footer = createHousingUnitDetailFooterDom(
      activeHousingUnit,
      state.community,
      lot,
      state.selectedStructuralOptions,
    );
    state.panel.appendChild(state.footer.getElement());
  }

  function switchTab(tabValue) {
    state.selectedTab = tabValue;
    state.tabs?.setActiveTab(tabValue);

    state.lotAndElevationContent?.getElement().remove();
    state.configContent?.getElement().remove();

    if (tabValue === TAB_LOT_AND_ELEVATION) {
      state.panel.appendChild(state.lotAndElevationContent.getElement());
    } else if (tabValue === TAB_FLOOR_PLAN_CONFIG) {
      state.panel.appendChild(state.configContent.getElement());
    }

    syncFooter();
    syncSecondaryPanel();
  }

  // When the active unit has no floor plan images there's nothing to switch to, so per design
  // (NHS20-8122) both tabs are hidden entirely rather than just disabling the Configuration one —
  // the Lot & Elevation content is shown directly, with no tab bar above it.
  function syncFloorPlanConfigTabState() {
    if (!state.tabs) {
      return;
    }

    const activeHousingUnit = selectActiveHousingUnit(state.getState());
    const hasFloorPlanConfigurations = Array.isArray(activeHousingUnit?.floorplans) &&
      activeHousingUnit.floorplans.length > 0;

    if (hasFloorPlanConfigurations) {
      if (!state.isImageExpanded) {
        state.tabs.show();
      }
    } else {
      state.tabs.hide();
    }

    // Hiding the tabs while the Configuration one is active (e.g. the user switches to a unit
    // with no floorplans while already on it) would otherwise leave the user stranded on a tab
    // that no longer has a way back — fall back to Lot & Elevation instead.
    if (!hasFloorPlanConfigurations && state.selectedTab === TAB_FLOOR_PLAN_CONFIG) {
      switchTab(TAB_LOT_AND_ELEVATION);
    }
  }

  function setImageExpanded(expanded) {
    if (expanded === state.isImageExpanded) {
      return;
    }
    state.isImageExpanded = expanded;

    if (expanded) {
      state.header.hide();
      state.tabs.hide();
      state.configContent.hide();
      syncFooter();
      state.panel.appendChild(state.floorPlanViewer.getElement());
      state.floorPlanViewer.setImage(selectActiveHousingUnit(state.getState()));
    } else {
      state.header.show();
      syncFloorPlanConfigTabState();
      state.floorPlanViewer.getElement().remove();
      state.configContent.show();
      syncFooter();
    }
  }

  function setupExpandButtonClickListener() {
    state.subscriptions.push(
      fromEvent(state.panel, 'click')
        .pipe(filter((event) => event.target.closest('[data-image-expand-button]') !== null))
        .subscribe(() => setImageExpanded(true)),
    );
  }

  function initializeContent() {
    const activeHousingUnit = selectActiveHousingUnit(state.getState());
    const lot = selectCurrentlySelectedHousingUnitLot(state.getState());
    state.lotAndElevationContent = createLotAndElevationDom(activeHousingUnit, lot);
    state.configContent = createFloorPlanConfigurationDom();

    if (state.isMobile) {
      state.configContent.getEmptyStateElement()
        .insertAdjacentHTML('beforeend', renderViewFloorPlanButton());
    }
  }

  function createTabs() {
    state.tabs = createTabsDom([
      {
        label: 'Lot & elevation',
        count: null,
        tabValue: TAB_LOT_AND_ELEVATION,
        isActive: true,
      },
      {
        label: 'Floor plan',
        count: null,
        tabValue: TAB_FLOOR_PLAN_CONFIG,
        isActive: false,
      },
    ]);

    state.tabs.attachEventListeners(
      createTabInteractionHandlers((tabValue) => {
        detailTabClicked$.next({ tabValue });
        switchTab(tabValue);
      })
    );

    const activeContent = state.selectedTab === TAB_LOT_AND_ELEVATION
      ? state.lotAndElevationContent
      : state.configContent;
    state.panel.insertBefore(state.tabs.getElement(), activeContent.getElement());
    syncFloorPlanConfigTabState();
  }

  function updateHeaderContent() {
    const activeHousingUnit = selectActiveHousingUnit(state.getState());
    if (state.header && activeHousingUnit) {
      state.header.setTitle(activeHousingUnit.name);
      state.header.setDetails({
        bedrooms: activeHousingUnit.bedrooms,
        bathrooms: activeHousingUnit.formattedBathrooms,
        squareFeet: activeHousingUnit.formattedSquareFeet,
      });
    }
  }

  function mount() {
    state.header = createHousingUnitDetailHeader();
    state.panel.appendChild(state.header.getElement());
    state.header.onBackClick(() => {
      state.dispatch(state.dataActions.navigateBack());
    });
    updateHeaderContent();

    initializeContent();
    state.panel.appendChild(state.lotAndElevationContent.getElement());
    createTabs();
    syncFooter();
    syncSecondaryPanel();
  }

  function remove() {
    state.header?.remove();
    state.header = null;

    state.lotAndElevationContent?.remove();
    state.lotAndElevationContent = null;

    state.configContent?.remove();
    state.configContent = null;

    state.tabs?.remove();
    state.tabs = null;

    state.footer?.remove();
    state.footer = null;

    state.floorPlanViewer?.getElement().remove();
    state.secondaryPanelController?.remove();
    state.secondaryPanelController = null;

    state.selectedTab = TAB_LOT_AND_ELEVATION;
    state.isImageExpanded = false;
    state.selectedStructuralOptions = null;
  }

  function onActiveViewChanged(activeView) {
    if (activeView === VIEW_HOUSING_UNIT_DETAIL) {
      mount();
    } else {
      remove();
    }
  }

  return {
    name: 'housing-unit-detail',

    async init(context) {
      const housingUnitCardSelectedEpic = createHousingUnitCardSelectedEpic({
        dataActions: context.services.dataActions,
        store$: context.services.store$,
      });
      registerEpicOnce(context.services.epic$, 'housing-unit-card-selected', housingUnitCardSelectedEpic);
    },

    async start(context) {
      state.panel = context.root.querySelector('[data-panel]');

      if (!state.panel) {
        return;
      }

      state.isMobile = context.state.isMobile;
      state.community = context.state.community;

      // Only ever created on desktop — the secondary panel doesn't exist at all on mobile, since
      // it's never shown there (see syncSecondaryPanel()). Created/destroyed per syncSecondaryPanel(),
      // not eagerly here — see secondaryPanelController.dom.js for why.
      state.mapContainer = context.root.querySelector('[data-map-container]');
      state.floorPlanViewer = createFloorPlanViewer({ onBackClick: () => setImageExpanded(false) });

      state.store$ = context.services.store$;
      state.getState = context.services.getState;
      state.dispatch = context.services.dispatch;
      state.dataActions = context.services.dataActions;

      setupCardClickListener();
      setupExpandButtonClickListener();

      state.subscriptions.push(
        state.store$.pipe(
          map(selectActiveView),
          distinctUntilChanged(),
        ).subscribe(onActiveViewChanged),
      );

      state.subscriptions.push(
        state.store$.pipe(
          filter((currentState) => selectActiveView(currentState) === VIEW_HOUSING_UNIT_DETAIL),
          map(selectActiveHousingUnit),
          distinctUntilChanged(),
        ).subscribe(() => {
          updateHeaderContent();
          syncFooter();
          syncSecondaryPanel();
          syncFloorPlanConfigTabState();
        }),
      );
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      teardownCardClickListener();
      remove();
      state.floorPlanViewer?.remove();
      state.floorPlanViewer = null;
      state.panel = null;
      state.mapContainer = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
      state.header = null;
    },
  };
}
